import { useMutation } from "@apollo/client/react"
import { DateTime } from "luxon"
import { useCallback, useMemo, useState } from "react"
import { useMatch, useNavigate } from "react-router-dom"

import {
  PublicCancelReservationDocument,
  PublicCancelReservationErrorEnum,
  PublicReservationStatus,
  type GetPublicReservationQuery,
} from "@/graphql/graphql"
import { usePublicReservation } from "@/graphql/hooks"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

export type CancelModalState = "confirm" | "blocked" | "success" | null

type Reservation = NonNullable<GetPublicReservationQuery["public"]["reservation"]>
type RefetchReservation = ReturnType<typeof usePublicReservation>["refetch"]

type Props = {
  refetch: RefetchReservation
  reservation: Reservation | null | undefined
  reservationId: string | undefined
  timezone: string | undefined
  token: string
}

export function useCancellationFlow({ refetch, reservation, reservationId, timezone, token }: Props) {
  const navigate = useNavigate()
  const cancelRouteMatch = useMatch("/r/:reservationId/cancel")
  const [resultState, setResultState] = useState<Exclude<CancelModalState, "confirm" | null>>()
  const [cancelReservation, { loading: cancelLoading }] = useMutation(PublicCancelReservationDocument)

  const cancelModalState: CancelModalState = useMemo(() => {
    if (resultState) return resultState
    if (cancelRouteMatch) return reservation?.canCancel === false ? "blocked" : "confirm"

    return null
  }, [cancelRouteMatch, reservation?.canCancel, resultState])

  const closeCancelModal = useCallback(() => {
    setResultState(undefined)

    if (cancelRouteMatch && reservationId) {
      navigate(`/r/${reservationId}`, { replace: true })
    }
  }, [cancelRouteMatch, navigate, reservationId])

  const handleCancelClick = useCallback(() => {
    if (!reservationId) return

    navigate(`/r/${reservationId}/cancel`)
  }, [navigate, reservationId])

  const handleConfirmCancellation = useCallback(async () => {
    if (!reservationId || cancelLoading) return

    try {
      const result = await cancelReservation({ variables: { reservationId, token } })
      const errors = result.data?.publicCancelReservation?.errors || []

      if (errors.length > 0) {
        const errorCode = errors[0]?.code

        if (
          errorCode === PublicCancelReservationErrorEnum.CancellationDeadlinePassed ||
          errorCode === PublicCancelReservationErrorEnum.NotCancellable
        ) {
          await refetch()
          setResultState("blocked")
          return
        }

        closeCancelModal()
        return
      }

      await refetch()
      setResultState("success")
    } catch (error) {
      showErrorToast()
      console.error(error)
      closeCancelModal()
    }
  }, [cancelLoading, cancelReservation, closeCancelModal, refetch, reservationId, token])

  const showCancelAction = reservation?.canStartCancellation || false

  const cancellationDeadlineLabel = useMemo(() => {
    if (!reservation?.cancellationDeadlineAt || !timezone || reservation.status === PublicReservationStatus.Cancelled) {
      return null
    }

    const formattedDeadline = DateTime.fromISO(reservation.cancellationDeadlineAt).setZone(timezone).toFormat("HH:mm")

    return i18n.t("app.public.reservation.cancellation_deadline_label", { time: formattedDeadline })
  }, [reservation?.cancellationDeadlineAt, reservation?.status, timezone])

  const cancelContactHref = useMemo(() => {
    if (reservation?.venue?.info?.email) return `mailto:${reservation.venue.info.email}`
    if (reservation?.venue?.info?.phone) return `tel:${reservation.venue.info.phone}`
    if (reservation?.venue?.slug) return `/v/${reservation.venue.slug}`

    return undefined
  }, [reservation?.venue?.info?.email, reservation?.venue?.info?.phone, reservation?.venue?.slug])

  return {
    cancelContactHref,
    cancelLoading,
    cancellationDeadlineLabel,
    cancelModalState,
    closeCancelModal,
    handleCancelClick,
    handleConfirmCancellation,
    showCancelAction,
  }
}
